import React, { Fragment } from 'react'
import { graphql } from 'gatsby'
import { keyframes } from '@emotion/core'
import Img from 'gatsby-image'

import { getProject } from '../../projects'
import {
  ProjectLayout,
  ProjectIntro,
  Title,
  Paragraph,
  dropShadow,
} from '../../layouts/project'

import { useNavbarTheme } from '../../contexts/NavbarTheme'

import theme from '../../styles/theme'
import { media, min, rem, selection, lines } from '../../styles/tools'

import { AutoFade, AutoSlide, AutoScale } from '../../components/Animated'
import Wrapper, { wrapperPadding } from '../../components/Wrapper'
import Space from '../../components/Space'
import WhenVisible from '../../components/WhenVisible'
import StaticVideo from '../../components/StaticVideo'
import Parallax from '../../components/Parallax'
import BackgroundText from '../../components/BackgroundText'
import Link from '../../components/Link'
import { Headline, BoldText, Detail } from '../../styles/typography'

import Logo from '../../assets/pages/work/ac-hotels/Logo.js'
import video from '../../assets/pages/work/ac-hotels/video.mp4'

export const query = graphql`
  {
    headerImage: file(relativePath: { eq: "pages/work/ac-hotels.jpg" }) {
      ...windowWide
    }
    facebookImage: file(relativePath: { eq: "pages/work/ac-hotels.jpg" }) {
      ...facebookShareImage
    }
    twitterImage: file(relativePath: { eq: "pages/work/ac-hotels.jpg" }) {
      ...twitterShareImage
    }
    devices: file(relativePath: { eq: "pages/work/ac-hotels/devices.png" }) {
      ...smallImage
    }
    city: file(relativePath: { eq: "pages/work/ac-hotels/city.png" }) {
      ...smallImage
    }
    ipad: file(relativePath: { eq: "pages/work/ac-hotels/ipad.png" }) {
      ...smallImage
    }
    webpage: file(relativePath: { eq: "pages/work/ac-hotels/webpage.png" }) {
      ...smallImage
    }
    img01: file(relativePath: { eq: "pages/work/ac-hotels/img01.png" }) {
      ...smallImage
    }
    img02: file(relativePath: { eq: "pages/work/ac-hotels/img02.png" }) {
      ...smallImage
    }
    img03: file(relativePath: { eq: "pages/work/ac-hotels/img03.png" }) {
      ...smallImage
    }
  }
`

let project = getProject('ac-hotels')

export default ({ data }) => {
  project = {
    ...project,
    headerImage: data.headerImage.childImageSharp.fluid,
    facebookImage: data.facebookImage.childImageSharp.fixed.src,
    twitterImage: data.twitterImage.childImageSharp.fixed.src,
    logo: <Logo />,
  }

  const devices = data.devices.childImageSharp.fluid
  const city = data.city.childImageSharp.fluid
  const ipad = data.ipad.childImageSharp.fluid
  const webpage = data.webpage.childImageSharp.fluid
  const img01 = data.img01.childImageSharp.fluid
  const img02 = data.img02.childImageSharp.fluid
  const img03 = data.img03.childImageSharp.fluid

  useNavbarTheme('dark')

  return (
    <ProjectLayout project={project}>
      <ProjectIntro
        project={project}
        text={
          <Fragment>
            <AutoFade>
              <Title as="h1" css={{ maxWidth: '14em' }}>
                Revitalizing Wilshire Blvd for&nbsp;the modern entrepreneur
              </Title>
            </AutoFade>
            <Space y={32} />
            <AutoFade>
              <Paragraph>
                We helped our agency partner bring Marriott’s newest brand, AC
                Hotel to historic Wilshire Blvd. With the recent growth and
                development of design-forward lifestyle hotels in the Los
                Angeles market, we knew we had to intelligently showcase how AC
                Hotels would benefit the community. Such as energizing and
                bringing much-needed tax revenue for the area. The campaign
                focused on the brand's key positioning – a conveniently located,
                upscale business hotel designed with the modern entrepreneur in
                mind. We built a microsite featuring an interactive 360º viewer
                giving the audience a sneak peek of hotel's guest rooms and
                amenities encapsulating AC’s mission “European-inspired classic
                modernism.”
              </Paragraph>
            </AutoFade>
          </Fragment>
        }
      >
        <Space y={{ mobile: 60, tablet: 100 }} />
        <AutoSlide
          up
          css={{
            margin: 'auto',
            marginBottom: '-40vmin',
            marginLeft: '-7%',
          }}
        >
          <Img fluid={devices} />
        </AutoSlide>
      </ProjectIntro>
      <Wrapper padded css={{ minHeight: '100vh' }}>
        <Space y="40vmin" />
        <Space y={64} />
        <BackgroundText
          as="h4"
          css={{ [media.max.tablet]: { display: 'none' } }}
        >
          DESIGN
        </BackgroundText>
        <WhenVisible>
          {inView => (
            <div
              css={{
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
                marginTop: '-7vw',
                transition: '1s ease-out',
                opacity: inView ? 1 : 0,
              }}
            >
              <div css={{ width: '85%', position: 'relative', zIndex: 1 }}>
                <Img fluid={img01} />
              </div>
              <div
                css={{
                  width: '65%',
                  position: 'absolute',
                  left: '-10%',
                  top: '9%',
                  transition: '1s .5s ease-out',
                  transform: inView ? '' : 'translateX(30%)',
                }}
              >
                <Img fluid={img02} />
              </div>
              <div
                css={{
                  width: '65%',
                  position: 'absolute',
                  right: '-10%',
                  top: '9%',
                  transition: '1s .5s ease-out',
                  transform: inView ? '' : 'translateX(-30%)',
                }}
              >
                <Img fluid={img03} />
              </div>
            </div>
          )}
        </WhenVisible>
        <Space y={{ mobile: 60, tablet: 120 }} />
        <AutoFade>
          <Img fluid={webpage} />
        </AutoFade>
        <Space y={{ mobile: 60, tablet: 120 }} />
        <BackgroundText
          as="h4"
          css={{ [media.max.tablet]: { display: 'none' } }}
        >
          360º VIEWER
        </BackgroundText>
      </Wrapper>
      <div
        css={{
          marginTop: '-7vw',
          padding: '0 16.666% 20vw',
          background: `url(${city.src}) center / cover #0a0a0a`,
        }}
      >
        <AutoSlide up style={{ position: 'relative' }}>
          <Img fluid={ipad} />
          <StaticVideo
            src={video}
            css={{
              width: '78%',
              position: 'absolute',
              top: '13.8%',
              left: '10.5%',
            }}
            ratio={248 / 371}
          />
        </AutoSlide>
      </div>
      <Parallax
        direction="both"
        distance={0.3}
        background={
          <div
            css={[
              lines('rgba(255, 255, 255, .06)'),
              {
                display: 'flex',
                alignItems: 'center',
                height: '130%',
                userSelect: 'none',
                backgroundColor: project.color,
              },
            ]}
          >
            <div
              css={{
                width: '80%',
                position: 'relative',
                left: '-9%',
                [media.tabletLg]: {
                  width: '45%',
                },
              }}
            >
              <svg viewBox="0 0 46.9 46.9" fill="#bfbdb2">
                <path d="M46.2 37.9v8.3H.8v-8.3h45.4m.7-.8H0v9.8h46.9v-9.8zM46.2.8v36.4H.8V.8h45.4m.7-.8H0v37.9h46.9V0zM40 11l-1.2.8-.1-.2a6.3 6.3 0 0 0-4.4-3.2c-2.2-.5-4.5 0-6.3 1.4a6 6 0 0 0-2.3 3.7 6.6 6.6 0 0 0 5.1 8.1c1.5.3 3 .2 4.4-.2a6.7 6.7 0 0 0 3.6-2.8c.3-.5.3-.5.9-.3l.7.4-.1.2a8.4 8.4 0 0 1-5.4 4 12 12 0 0 1-3.8.2c-1.2-.1-2.3-.5-3.4-1-1.8-1-3-2.7-3.5-4.6-.6-1.8-.4-3.8.4-5.6a7.5 7.5 0 0 1 4.3-4.1A8.3 8.3 0 0 1 37 8c1.3.7 2.3 1.7 3 3m-20.7 6.4l-4.4-8.7-4.3 8.7h8.7zm4.1 5.2h-1.5a.2.2 0 0 1-.2-.2L20 19a.5.5 0 0 0-.5-.3h-9.2c-.2 0-.4.1-.4.3l-1.7 3.5-.3.2H6.6l.3-.7 3.6-7.4 3.4-6.9c.1-.2.3-.3.5-.3h1.1l.3.2 2.9 5.9 4.6 9 .1.1m-6.6 6.1c0-.9-.6-1.6-1.5-1.6h-.2c-.9-.1-1.7.6-1.7 1.5-.1.9.6 1.7 1.5 1.7h.3c.8.1 1.6-.5 1.7-1.4l-.1-.2m-1.6 2.1c-.8.1-1.5-.2-2-.8-.7-.9-.5-2.3.4-3 .2-.2.4-.3.7-.3.8-.3 1.7-.2 2.4.3 1 .7 1.2 2 .5 3-.5.5-1.2.9-2 .8m13.6-1.9h-2.3c-.5 0-.5 0-.5.5v.7h3c.3 0 .3.1.3.3s0 .3-.3.3h-3.6v-4H29v.5h-3v1.1h2.5c.4 0 .4 0 .4.4l-.1.2m-18.8 0H7.5c-.3 0-.3 0-.3.3v1.4c0 .2-.1.3-.3.3h-.3v-4.1h.6l.1.2v1.2c0 .2.1.3.3.3h2.2c.2 0 .3 0 .3-.3v-1.4h.7v3.7c0 .2-.1.3-.3.3H10v-1.9zm26.5.4l.6-.1c0 .4.3.7.6.9.5.2 1 .2 1.5 0 .3-.1.5-.3.4-.6 0-.3-.2-.5-.5-.5l-1.6-.4c-.7-.1-.9-.5-.9-.9 0-.5.3-.9.8-1.1a3 3 0 0 1 1.9 0c.5.2.8.6.8 1.1h-.5l-.1-.1c-.1-.4-.4-.6-.8-.7a2 2 0 0 0-1.1.1l-.3.4c0 .2.1.3.3.4l1.1.3.9.2c.4.1.6.4.6.8.1.4-.1.9-.5 1.1-.8.4-1.8.4-2.7 0-.2 0-.5-.4-.5-.9m-17-2.1v-.5h3.8v.5h-1.5V30.4c0 .2-.1.3-.3.3h-.1c-.2 0-.3 0-.3-.3v-3c0-.2-.1-.3-.3-.3l-1.3.1m11.7-.6h.6v3.1c0 .5 0 .4.4.4h2.2v.1c.1.5.1.5-.5.5h-2.8l.1-4.1zM18 41.2v.8l.1.2c.3.1.6.1.9-.1.4-.5.3-1.2-.1-1.6l-.6-.2-.2.2-.1.7m4.7 1c.3.1.7-.2.7-.5v-.8c-.1-.4-.4-.7-.8-.7-.3 0-.3.1-.3.3v1.2c-.1.4-.1.5.4.5M21.1 40h2.1c.6 0 1 .5 1 1l-.2.6-.4.5v.3l1 1.2c.2.3.5.5.9.5.3 0 .4-.1.4-.3v-3.2c0-.3-.1-.5-.5-.5l.1-.1h1.6c-.4.1-.4.3-.4.6V43.8c0 .2.1.4.3.4h.1l.2.1h-2.1c-.5 0-1-.2-1.2-.6l-.7-1c-.2-.2-.4-.3-.7-.2l-.1.2v1.1c0 .3.1.4.4.4l.1.1h-2.1c-.5 0-.9-.2-1.2-.6l-.6-.8c-.1-.3-.5-.5-.8-.3V44l.2.2.2.1h-1.5c.4-.1.4-.4.4-.7v-3c0-.4 0-.4-.4-.5l-.1-.1H17h1.4l1 .2c.5.2.7.8.5 1.2l-.5.7-.2.2 1.3 1.6c.2.2.5.3.7.3s.4 0 .4-.3v-.6-2.5c0-.5 0-.5-.5-.7m-6.3 2.4l-.5-1.4h-.1l-.5 1.4h1.1zm-.4-2.7l.3.7 1.2 2.9c.2.5.3.6.8.8h-1.3l-.3-1c-.2-.6-.1-.5-.7-.5-.8 0-.8 0-1.1.7l-.1.2c-.1.3-.1.4.3.5h-2.4l-.4-2.8h-.1l-1.4 2.8-1.4-2.7-.1.5-.2 1.7c0 .3 0 .4.3.4l.1.1H6.6c.7-.2.5-.8.6-1.3l.5-2.9.1-.3.2.3 1.3 2.8.2.3 1.6-3.3h.1l.1.6.5 3.2c0 .2.1.4.4.5.3.1.5-.1.6-.3l1-2.5.4-1.1.2-.3m17.1 2.4l-.2-.9c-.2-.8-1-1.2-1.7-1.1-.6.1-1 .5-1.1 1.1-.2.8 0 1.7.6 2.3.5.5 1.3.6 1.9.1l.4-.7.1-.8m.8-.2c0 1.2-.9 2.3-2.2 2.3H30c-1.2 0-2.2-1-2.2-2.2s1-2.2 2.2-2.2c1.1-.2 2.1.6 2.3 1.7v.4m4-1.3c-.1-.4-.4-.4-.7-.5h-.2c-.6 0-.6 0-.6.6v2.6c0 .6 0 .6.5.8h-1.6c.4-.1.4-.4.4-.7v-3c0-.2-.1-.3-.3-.3h-.5c-.2 0-.6 0-.8.4l.1-.7h3.6l.1.8zm3.1 3.5h-1.7c.4-.1.4-.4.4-.7v-3c0-.2-.1-.3-.3-.3h-.3c-.4 0-.8-.1-1 .4h-.1l.1-.7h3.6v.8c-.1-.4-.5-.4-.8-.4h-.6l-.1.7v2.5c.3.4.3.5.8.7" />
              </svg>
            </div>
          </div>
        }
      >
        <div
          css={[
            wrapperPadding,
            selection(theme.colors.white, project.color),
            {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '65vh',
              maxHeight: rem(600),
              color: theme.colors.white,
            },
          ]}
        >
          <AutoSlide up translate={50}>
            <Title
              as="blockquote"
              css={{ maxWidth: '21em', textAlign: 'center' }}
            >
              Our work on the AC Hotel proposal helped activate members of the
              neighborhood to show support for the project that would revitalize
              Wilshire Blvd.
            </Title>
          </AutoSlide>
        </div>
      </Parallax>
    </ProjectLayout>
  )
}
